import Facet from "../types/funnelback/Facets";
import searchConfigFile from "./config.json";
import {
    FacetUiConfig,
    ResultColumn,
    SearchCollectionConfigEntry,
    SearchCollectionDefinition,
    SearchConfigFile,
} from "./types";

const searchConfig = searchConfigFile as SearchConfigFile;

const resolveShared = <T>(
    value: string | T | undefined,
    sharedMap: Record<string, T>
): T | undefined => {
    if (value === undefined) {
        return undefined;
    }
    if (typeof value === "string") {
        return sharedMap[value];
    }
    return value;
};

const resolveCollectionEntry = (
    entry: SearchCollectionConfigEntry
): Partial<SearchCollectionDefinition> => {
    const { shared } = searchConfig;

    return {
        ...entry,
        itemDefs: resolveShared(entry.itemDefs, shared.itemDefs),
        fetchParams: resolveShared(entry.fetchParams, shared.fetchParams),
        facetInjectBeforeCheckbox: resolveShared(
            entry.facetInjectBeforeCheckbox,
            shared.facetInjections
        ) as Facet[] | undefined,
        facetUi: resolveShared(entry.facetUi, shared.facetUi),
    };
};

export const getSearchCollectionConfig = (
    collectionId: string
): SearchCollectionDefinition => {
    const entry = searchConfig.collections[collectionId];
    const resolved = entry ? resolveCollectionEntry(entry) : {};

    return {
        ...searchConfig.defaults,
        ...resolved,
        itemDefs: resolved.itemDefs ?? searchConfig.defaults.itemDefs,
    };
};

const orderFacets = (
    facets: Facet[],
    desiredOrder: string[],
    mode: "strict" | "ordered-then-rest"
): Facet[] => {
    const nameToPosition: Record<string, number> = {};
    facets.forEach((item, index) => {
        nameToPosition[item.name] = index;
    });

    const ordered = desiredOrder
        .map((name) => nameToPosition[name])
        .filter((index) => index !== undefined && index !== null)
        .map((index) => facets[index]);

    if (mode === "ordered-then-rest") {
        const orderedNames = new Set(desiredOrder);
        const rest = facets.filter((f) => !orderedNames.has(f.name));
        return [...ordered, ...rest];
    }

    return ordered;
};

export const resolveFacetsForCollection = (
    facets: Facet[] | undefined,
    collectionId: string
): Facet[] => {
    if (!facets) {
        return [];
    }

    const config = getSearchCollectionConfig(collectionId);
    let resolvedFacets = facets;

    if (config.facetInjectBeforeCheckbox?.length) {
        const newFacets: Facet[] = [];
        for (const facet of facets) {
            if (facet.guessedDisplayType === "CHECKBOX") {
                newFacets.push(...config.facetInjectBeforeCheckbox);
            }
            newFacets.push(facet);
        }
        resolvedFacets = newFacets;
    }

    if (config.facetOrder?.length && config.facetOrderMode) {
        return orderFacets(
            resolvedFacets,
            config.facetOrder,
            config.facetOrderMode
        );
    }

    return resolvedFacets;
};

export const isGlobalSearchCollection = (collectionId: string): boolean =>
    getSearchCollectionConfig(collectionId).displayMode === "global";

export const isCardSearchCollection = (collectionId: string): boolean =>
    getSearchCollectionConfig(collectionId).displayMode === "card";

export const shouldOmitShowAllQuery = (collectionId: string): boolean =>
    !!getSearchCollectionConfig(collectionId).omitShowAllQuery;

export const getSelectFacetColumnClass = (
    collectionId: string,
    label: string
): string => {
    const { facetUi } = getSearchCollectionConfig(collectionId);
    return facetUi?.selectColumnClasses?.[label] ?? "col-xs-12 col-md-4";
};

export const getMultiSelectFacetColumnClass = (
    collectionId: string,
    label: string
): string => {
    const { facetUi } = getSearchCollectionConfig(collectionId);
    return facetUi?.multiSelectColumnClasses?.[label] ?? "col-xs-12 col-md-4";
};

export const getSelectFacetPlaceholder = (
    collectionId: string,
    label: string
): string => {
    const { facetUi } = getSearchCollectionConfig(collectionId);
    const configuredPlaceholder = facetUi?.facetPlaceholders?.[label];
    if (configuredPlaceholder) {
        return configuredPlaceholder;
    }
    return `Select ${label.toLowerCase()}`;
};

export const shouldHideCheckboxGroupLegend = (
    collectionId: string
): boolean =>
    !!getSearchCollectionConfig(collectionId).facetUi?.hideCheckboxGroupLegend;

export const getCollectionResultsClass = (
    collectionId: string
): string | undefined => getSearchCollectionConfig(collectionId).resultsClass;

export type {
    FacetUiConfig,
    ResultColumn,
    SearchCollectionDefinition,
    SearchConfigFile,
};
